import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Sheet,
    Typography,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    Box,
    Stack,
    Divider,
    Alert,
    IconButton,
    CircularProgress
} from '@mui/joy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import domain from '../utils/domain';

interface ExamFormData {
    title: string;
    description: string;
    duration: number;
    totalMarks: number;
    passingMarks: number;
    negativeMarking: number;
    instructions: string;
}

const EditExam: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState<ExamFormData>({
        title: '',
        description: '',
        duration: 75,
        totalMarks: 100,
        passingMarks: 35,
        negativeMarking: 0.25,
        instructions: ''
    });

    useEffect(() => {
        fetchExamData();
    }, [id]);

    const fetchExamData = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/exams/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch exam data');
            }

            const data = await response.json();
            setFormData({
                title: data.exam.title,
                description: data.exam.description || '',
                duration: data.exam.duration,
                totalMarks: data.exam.total_marks,
                passingMarks: data.exam.passing_marks,
                negativeMarking: data.exam.negative_marking,
                instructions: data.exam.instructions || ''
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load exam data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(`${domain}/api/admin/exams/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to update exam');
            }

            navigate('/admin/dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update exam');
            if (err instanceof Error && err.message === 'Authentication token not found') {
                navigate('/admin');
            }
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.level1', py: 4 }}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    p: 4,
                    borderRadius: 'lg',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton
                        onClick={() => navigate('/admin/dashboard')}
                        variant="plain"
                        size="sm"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography level="h3">Edit Exam</Typography>
                </Box>

                {error && (
                    <Alert color="danger" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <FormControl required>
                            <FormLabel>Exam Title</FormLabel>
                            <Input
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter exam title"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                minRows={3}
                                placeholder="Enter exam description"
                            />
                        </FormControl>

                        <Divider>Exam Settings</Divider>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <FormControl required>
                                <FormLabel>Duration (minutes)</FormLabel>
                                <Input
                                    type="number"
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 1
                                        }
                                    }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Total Marks</FormLabel>
                                <Input
                                    type="number"
                                    name="totalMarks"
                                    value={formData.totalMarks}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 1
                                        }
                                    }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Passing Marks</FormLabel>
                                <Input
                                    type="number"
                                    name="passingMarks"
                                    value={formData.passingMarks}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 1
                                        }
                                    }}
                                />
                            </FormControl>

                            <FormControl required>
                                <FormLabel>Negative Marking</FormLabel>
                                <Input
                                    type="number"
                                    name="negativeMarking"
                                    value={formData.negativeMarking}
                                    onChange={handleInputChange}
                                    slotProps={{
                                        input: {
                                            min: 0,
                                            step: 0.25
                                        }
                                    }}
                                />
                            </FormControl>
                        </Box>

                        <FormControl>
                            <FormLabel>Instructions</FormLabel>
                            <Textarea
                                name="instructions"
                                value={formData.instructions}
                                onChange={handleInputChange}
                                minRows={4}
                                placeholder="Enter exam instructions"
                            />
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => navigate('/admin/dashboard')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={isSaving}
                                loadingPosition="start"
                            >
                                Save Changes
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </Sheet>
        </Box>
    );
};

export default EditExam; 